import React, { type PropsWithChildren, useEffect, useState } from 'react';

import { connect } from 'react-redux';

import SpinnerV2 from 'common/SpinnerV2';
import AdminV2ErrorView from 'common/subdomain/admin/v2/AdminV2ErrorView';
import devURL from 'common/util/devURL';

import type { Company } from 'common/api/endpoints/companies';
import type { Viewer } from 'common/api/endpoints/viewer';
import type { State } from 'redux-connect';

type withReducerProps<T> = Omit<T, 'loading'> & {
  error: string | null;
  loading: boolean;
};

type Props = {
  viewer: withReducerProps<Viewer>;
  company: withReducerProps<Company & { notFound: boolean }>;
};

const PrivateContainer = ({ viewer, company, children }: PropsWithChildren<Props>) => {
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (viewer.loggedOut) {
      setIsRedirecting(true);
      window.location.assign(devURL('https://canny.io/login'));
    }
  }, [viewer]);

  if (viewer.loggedOut || isRedirecting) {
    return null;
  }

  if (viewer.loading || company.loading) {
    return (
      <Container>
        <SpinnerV2 />
      </Container>
    );
  }

  if (company.notFound) {
    return <Container>There is no such company. Did you enter the right URL?</Container>;
  }

  if (viewer.error || company.error) {
    return (
      <Container>
        <AdminV2ErrorView />
      </Container>
    );
  }

  if (!company.viewerIsMember) {
    return <Container>You aren't a member of this company.</Container>;
  }

  return <>{children}</>;
};

const Container = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex h-screen w-screen items-center justify-center p-6">{children}</div>;
};

export default connect((state: State) => ({ viewer: state.viewer, company: state.company }))(
  PrivateContainer,
);
